import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import BannerImage from '../../components/banner/banner-image'
import ContentContainer from '../../areas/layout/content-container'
import TrailerModal from '../../components/trailer-modal/trailer-modal'
import SeasonCard from '../../components/cards/season-card'
import { useMediaState } from '../../context/media-state-context'
import { useShowDetails } from '../../hooks/useShowDetails'
import { ShowNotFound, LoadError } from '../Status/status-page'
import {
  StyledPageWrapper,
  StyledLoadingWrapper,
  StyledContentWrapper,
  StyledMainContent,
  StyledSectionTitle,
  SeasonsGrid,
  ProvidersRow,
  ProviderChip,
  ProviderLogo,
  SectionHeaderRow,
  SectionActionButton,
  EpisodeProgressBlock,
  EpisodeProgressLabel,
  EpisodeProgressNote,
  EpisodeProgressBar,
  ShowAllSeasonsButton,
  DialogActionButton,
  DialogConfirmButton,
} from '../../components/Details/details-page-styles'
import CastSection from '../../components/Details/cast-section'
import CommentsSection from '../../components/Details/comments-section'
import DetailsOverview from '../../components/Details/details-overview'
import RecommendationsSection from '../../components/Details/recommendations-section'
import DetailsSidebar from '../../components/Details/details-sidebar'
import DetailsHeader from '../../components/Details/details-header'
import { showProgressIndicators } from '../../utils/progress-visibility'
import { useAuth } from '../../context/AuthContext'
import { useUserRatings } from '../../hooks/useUserRatings'
import { useShowReminder } from '../../hooks/useShowReminder'
import { getReleaseInfo } from '../../utils/release-date'
import { useRecordBrowseView } from '../../hooks/use-record-browse-view'

const INITIAL_VISIBLE_SEASONS = 4

const ShowDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { data: show, loading, error, status, refetch } = useShowDetails(id)
  const [trailerOpen, setTrailerOpen] = useState(false)
  const [showAllSeasons, setShowAllSeasons] = useState(false)
  const [markAllDialogOpen, setMarkAllDialogOpen] = useState(false)
  const [markingAll, setMarkingAll] = useState(false)
  const { isAuthenticated } = useAuth()
  const {
    favoritesSet,
    watchlistSet,
    historySet,
    toggleFavorite,
    toggleWatchlist,
    toggleHistory,
    mediaState,
    isFavoritePending,
    isWatchlistPending,
    isHistoryPending,
    markAllEpisodesWatched,
  } = useMediaState()
  const { mediaRating, rateMedia } = useUserRatings(
    'Show',
    show?.id,
    isAuthenticated,
  )
  const { isReminding, isReminderPending, toggleReminder } = useShowReminder(
    show?.id ?? 0,
  )

  useRecordBrowseView(
    isAuthenticated,
    show
      ? {
          mediaType: 'Show',
          tmdbId: show.id,
          title: show.title,
          posterUrl: show.posterUrl,
          year: show.year,
          genres: show.genres,
        }
      : null,
  )

  if (loading) {
    return (
      <StyledLoadingWrapper>
        <CircularProgress />
      </StyledLoadingWrapper>
    )
  }

  if (error || !show) {
    return status && status !== 404 ? (
      <LoadError onRetry={refetch} />
    ) : (
      <ShowNotFound />
    )
  }

  const handleWatchTrailer = () => {
    setTrailerOpen(true)
  }

  const handleMarkAllWatched = async () => {
    setMarkingAll(true)
    try {
      await markAllEpisodesWatched(show.id)
    } finally {
      setMarkingAll(false)
      setMarkAllDialogOpen(false)
    }
  }

  const meta = {
    title: show.title,
    posterPath: show.posterUrl,
    releaseYear: show.year ? Number(show.year) : undefined,
    genres: show.genres,
  }

  const releaseInfo = getReleaseInfo(show.firstAirDate)

  // Separate Remind Me bell for new-episode alerts, shown only on released
  // shows that are still returning. Ended/canceled shows can't get new
  // episodes, so no bell. Independent from the watchlist button.
  const showConcluded = show.status === 'Ended' || show.status === 'Canceled'
  const remindable = !releaseInfo.unreleased && !showConcluded

  const historyEntry = mediaState?.history.find((h) => h.tmdbId === show.id)
  const totalEpisodes = show.numberOfEpisodes ?? 0
  const watchedEpisodes = historyEntry?.episodes?.length ?? 0
  const allEpisodesWatched =
    show.airedEpisodes > 0 && watchedEpisodes >= show.airedEpisodes
  const watchedProgress =
    totalEpisodes > 0
      ? Math.min(100, (watchedEpisodes / totalEpisodes) * 100)
      : 0

  const seasonWatchedMap =
    historyEntry?.episodes?.reduce<Record<number, number>>((acc, ep) => {
      acc[ep.season] = (acc[ep.season] || 0) + 1
      return acc
    }, {}) ?? {}

  return (
    <StyledPageWrapper>
      <BannerImage imageUrl={show.backdropUrl}>
        <ContentContainer>
          <DetailsHeader
            title={show.title}
            tagline={show.tagline}
            posterUrl={show.posterUrl}
            rating={show.rating}
            year={show.year}
            runtimeOrStatus={show.status || show.episodeRuntime}
            genres={show.genres}
            hasTrailer={!!show.trailerUrl}
            onWatchTrailer={show.trailerUrl ? handleWatchTrailer : undefined}
            isFavorite={favoritesSet.has(show.id)}
            isWatchlisted={watchlistSet.has(show.id)}
            isHistory={historySet.has(show.id)}
            onToggleFavorite={() =>
              meta && toggleFavorite(show.id, 'show', meta)
            }
            onToggleWatchlist={() =>
              meta && toggleWatchlist(show.id, 'show', meta)
            }
            onToggleHistory={() => meta && toggleHistory(show.id, 'show', meta)}
            actionsEnabled={isAuthenticated}
            disableWatchlist={
              historySet.has(show.id) || isWatchlistPending(show.id)
            }
            disableFavorite={isFavoritePending(show.id)}
            disableHistory={isHistoryPending(show.id)}
            showUserRating={isAuthenticated}
            userRatingLocked={!historySet.has(show.id)}
            userRating={mediaRating?.ratings_Value ?? null}
            onUserRatingChange={rateMedia}
            unreleased={releaseInfo.unreleased}
            releaseDateDisplay={releaseInfo.display}
            showRemindButton={isAuthenticated && remindable}
            isReminding={isReminding}
            onToggleRemind={() =>
              toggleReminder({ title: show.title, posterPath: show.posterUrl })
            }
            disableRemind={isReminderPending}
          />
        </ContentContainer>
      </BannerImage>

      <ContentContainer>
        <StyledContentWrapper>
          <StyledMainContent>
            <DetailsOverview overview={show.overview} />
            {isAuthenticated &&
              totalEpisodes > 0 &&
              showProgressIndicators() && (
                <EpisodeProgressBlock>
                  <EpisodeProgressLabel variant='body2' color='text.secondary'>
                    Episodes watched: {watchedEpisodes}/{totalEpisodes} (
                    {Math.floor(watchedProgress)}%)
                    <EpisodeProgressNote variant='caption'>
                      Special episodes are excluded from totals
                    </EpisodeProgressNote>
                  </EpisodeProgressLabel>
                  <EpisodeProgressBar
                    variant='determinate'
                    value={watchedProgress}
                  />
                </EpisodeProgressBlock>
              )}

            {show.watchProviders && show.watchProviders.length > 0 && (
              <ProvidersRow>
                {show.watchProviders.slice(0, 10).map((p) => (
                  <ProviderChip key={p.name}>
                    {p.logoUrl ? (
                      <ProviderLogo src={p.logoUrl} alt={p.name} />
                    ) : null}
                    {p.name}
                  </ProviderChip>
                ))}
              </ProvidersRow>
            )}

            {show.seasons && show.seasons.length > 0 && (
              <>
                <SectionHeaderRow>
                  <StyledSectionTitle>Seasons</StyledSectionTitle>
                  {isAuthenticated && !releaseInfo.unreleased && (
                    <SectionActionButton
                      variant='outlined'
                      size='small'
                      startIcon={<DoneAllRoundedIcon />}
                      onClick={() => setMarkAllDialogOpen(true)}
                      disabled={markingAll || allEpisodesWatched}
                    >
                      Mark all episodes watched
                    </SectionActionButton>
                  )}
                </SectionHeaderRow>
                <SeasonsGrid>
                  {(showAllSeasons
                    ? show.seasons
                    : show.seasons.slice(0, INITIAL_VISIBLE_SEASONS)
                  ).map((season) => (
                    <SeasonCard
                      key={season.id}
                      id={season.id}
                      name={season.name}
                      posterUrl={season.posterUrl}
                      seasonNumber={season.seasonNumber}
                      episodeCount={season.episodeCount}
                      watchedCount={seasonWatchedMap[season.seasonNumber] ?? 0}
                      to={`/shows/${show.id}/season/${season.seasonNumber}`}
                    />
                  ))}
                </SeasonsGrid>
                {!showAllSeasons &&
                  show.seasons.length > INITIAL_VISIBLE_SEASONS && (
                    <ShowAllSeasonsButton
                      disableRipple
                      onClick={() => setShowAllSeasons(true)}
                    >
                      Show all seasons
                    </ShowAllSeasonsButton>
                  )}
              </>
            )}

            <CastSection cast={show.cast} initialVisibleCount={10} />
          </StyledMainContent>

          <DetailsSidebar
            rows={
              [
                show.creator && { label: 'Creator', value: show.creator },
                show.network && { label: 'Network', value: show.network },
                show.firstAirDate && {
                  label: 'First Air Date',
                  value: show.firstAirDate,
                },
                show.lastEpisodeAirDate && {
                  label: 'Last Episode Air Date',
                  value: show.lastEpisodeAirDate,
                },
                show.nextEpisodeAirDate && {
                  label: 'Next Episode Air Date',
                  value: show.nextEpisodeAirDate,
                },
                show.numberOfSeasons && {
                  label: 'Seasons',
                  value: show.numberOfSeasons,
                },
                show.numberOfEpisodes && {
                  label: 'Episodes',
                  value: show.numberOfEpisodes,
                },
              ].filter(Boolean) as { label: string; value: string | number }[]
            }
          />
        </StyledContentWrapper>

        {/* Full width, outside the main/sidebar grid */}
        <CommentsSection
          mediaType='Show'
          tmdbMediaId={show.id}
          lockedReason={
            releaseInfo.unreleased
              ? 'Comments open once this show is released.'
              : undefined
          }
        />

        <RecommendationsSection
          title={`If you like ${show.title}, you might enjoy`}
          items={show.recommendations.map((rec) => ({
            ...rec,
            type: 'show' as const,
          }))}
        />
      </ContentContainer>

      <TrailerModal
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
        title={show.title}
        trailerUrl={show.trailerUrl}
      />

      <Dialog
        open={markAllDialogOpen}
        onClose={() => !markingAll && setMarkAllDialogOpen(false)}
      >
        <DialogTitle>Mark all episodes watched?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every aired episode of {show.title} will be added to your watched
            episodes. Specials and unaired episodes are skipped, and episodes
            you already watched stay untouched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogActionButton
            onClick={() => setMarkAllDialogOpen(false)}
            disabled={markingAll}
          >
            Cancel
          </DialogActionButton>
          <DialogConfirmButton
            variant='contained'
            onClick={handleMarkAllWatched}
            disabled={markingAll}
          >
            {markingAll ? 'Marking...' : 'Mark all watched'}
          </DialogConfirmButton>
        </DialogActions>
      </Dialog>
    </StyledPageWrapper>
  )
}

export default ShowDetailPage
